import { Link } from 'react-router-dom';
import type { FeaturedPost, ModelDetail } from '../../data/types';
import { formatPostDate } from '../../lib/format';
import styles from './FeaturedQuote.module.css';

interface FeaturedQuotesProps {
  models: ModelDetail[];
}

interface SelectedQuote {
  post: FeaturedPost;
  model: ModelDetail;
}

// Pick one strong quote per model. Prefer high-confidence posts. Within those,
// rotate sentiment so the homepage shows positive/mixed/negative if possible —
// it should not look like a press kit.
function selectQuotes(models: ModelDetail[]): SelectedQuote[] {
  const selected: SelectedQuote[] = [];
  const usedSentiments = new Set<string>();

  for (const model of models) {
    const candidates = model.featured_discourse.filter((p) => p.confidence === 'high');
    if (candidates.length === 0) continue;
    // Prefer a sentiment we have not used yet
    const fresh = candidates.find((p) => !usedSentiments.has(p.overall_sentiment)) ?? candidates[0];
    selected.push({ post: fresh, model });
    usedSentiments.add(fresh.overall_sentiment);
  }
  return selected.slice(0, 3);
}

export function FeaturedQuotes({ models }: FeaturedQuotesProps) {
  const quotes = selectQuotes(models);
  if (quotes.length === 0) return null;

  return (
    <section className={styles.section} aria-label="Featured discourse">
      <header className={styles.header}>
        <p className={styles.eyebrow}>Featured discourse</p>
        <h2 className={styles.heading}>From the public record.</h2>
        <p className={styles.subtitle}>
          Real posts the judge labelled with high confidence this week. Quoted as written;
          punctuation and grammar preserved.
        </p>
      </header>

      <ol className={styles.list}>
        {quotes.map(({ post, model }) => (
          <li key={post.post_key} className={styles.item}>
            <div className={styles.markRow}>
              <span className={`${styles.sentiment} ${styles[`sentiment_${post.overall_sentiment}`]}`}>
                {post.overall_sentiment.replace('_', ' ')}
              </span>
              <span className={styles.divider} aria-hidden="true">·</span>
              <Link to={`/models/${model.id}`} className={styles.modelChip}>
                {model.display_name}
              </Link>
            </div>

            <blockquote className={styles.quote}>
              <p>&ldquo;{post.text}&rdquo;</p>
            </blockquote>

            <p className={styles.attribution}>
              <a href={post.url} target="_blank" rel="noreferrer noopener" className={styles.attLink}>
                {post.platform}
              </a>
              <span className={styles.divider} aria-hidden="true">·</span>
              <span className={styles.attDate}>{formatPostDate(post.created_at)}</span>
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}
